import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Gift, Users, DollarSign, Heart, CheckCircle, ArrowRight, User } from 'lucide-react';
import { toast } from 'sonner';
import { submitReferral } from '../api/client';

const ReferralPage = () => {
  const [referrerData, setReferrerData] = useState({
    yourName: '',
    yourEmail: '',
    friendName: '',
    friendEmail: '',
    friendPhone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await submitReferral(referrerData);
      toast.success('Thank you! Your referral has been submitted. You\'ll both receive your discount codes via email!');
      setReferrerData({
        yourName: '',
        yourEmail: '',
        friendName: '',
        friendEmail: '',
        friendPhone: ''
      });
    } catch (error) {
      toast.error('Failed to submit referral. Please try again.');
      console.error('Referral error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-black text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black opacity-90"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <Gift className="w-20 h-20 text-red-600 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Refer a Friend, <span className="text-red-600">Save Together!</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Love our service? Share the clean! Give your friends 10% off their first cleaning, and you'll get 10% off your next service.
            </p>
            <div className="inline-flex items-center gap-3 bg-red-600 text-white px-8 py-4 rounded-lg text-2xl font-bold">
              <DollarSign className="w-8 h-8" />
              10% OFF for Both!
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              It's simple! Just three easy steps to save.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center border-2 hover:border-red-600 transition-colors">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  1
                </div>
                <h3 className="text-xl font-bold mb-3">Refer Your Friend</h3>
                <p className="text-gray-600">
                  Fill out the referral form below with your friend's information
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-2 hover:border-red-600 transition-colors">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  2
                </div>
                <h3 className="text-xl font-bold mb-3">They Book & Save</h3>
                <p className="text-gray-600">
                  Your friend receives 10% off their first cleaning service
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-2 hover:border-red-600 transition-colors">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  3
                </div>
                <h3 className="text-xl font-bold mb-3">You Get Rewarded</h3>
                <p className="text-gray-600">
                  You receive 10% off your next cleaning when they book
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Why Refer <span className="text-red-600">Taylormade?</span>
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-lg">40+ Years of Excellence</p>
                    <p className="text-gray-600">Trusted since 1983 with proven quality</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-lg">Licensed & Insured</p>
                    <p className="text-gray-600">Professional, background-checked team</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-lg">100% Satisfaction Guaranteed</p>
                    <p className="text-gray-600">We're not happy until you're delighted</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-lg">Eco-Friendly Products</p>
                    <p className="text-gray-600">Safe for families, pets, and the planet</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="text-center mb-6">
                <Heart className="w-16 h-16 text-red-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold">Share the Love</h3>
                <p className="text-gray-600 mt-2">
                  Help your friends discover a cleaner, happier home
                </p>
              </div>
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-green-900 font-semibold flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Unlimited Referrals
                  </p>
                  <p className="text-green-700 text-sm mt-1">
                    Refer as many friends as you'd like - there's no limit!
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-blue-900 font-semibold flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Stack Your Savings
                  </p>
                  <p className="text-blue-700 text-sm mt-1">
                    Each successful referral earns you another 10% off
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Referral Form */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-red-100">
            <CardHeader className="bg-red-50">
              <CardTitle className="text-2xl">Refer a Friend Now</CardTitle>
              <CardDescription>
                Fill out the form and we'll contact your friend with their exclusive 10% discount
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Your Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <User className="w-5 h-5 text-red-600" />
                    Your Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="yourName">Your Name *</Label>
                      <Input
                        id="yourName"
                        required
                        value={referrerData.yourName}
                        onChange={(e) => setReferrerData({ ...referrerData, yourName: e.target.value })}
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="yourEmail">Your Email *</Label>
                      <Input
                        id="yourEmail"
                        type="email"
                        required
                        value={referrerData.yourEmail}
                        onChange={(e) => setReferrerData({ ...referrerData, yourEmail: e.target.value })}
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Friend's Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-red-600" />
                    Friend's Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="friendName">Friend's Name *</Label>
                      <Input
                        id="friendName"
                        required
                        value={referrerData.friendName}
                        onChange={(e) => setReferrerData({ ...referrerData, friendName: e.target.value })}
                        placeholder="Jane Smith"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="friendEmail">Friend's Email *</Label>
                      <Input
                        id="friendEmail"
                        type="email"
                        required
                        value={referrerData.friendEmail}
                        onChange={(e) => setReferrerData({ ...referrerData, friendEmail: e.target.value })}
                        placeholder="friend@email.com"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="friendPhone">Friend's Phone Number (Optional)</Label>
                    <Input
                      id="friendPhone"
                      type="tel"
                      value={referrerData.friendPhone}
                      onChange={(e) => setReferrerData({ ...referrerData, friendPhone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting} 
                  className="w-full bg-red-600 hover:bg-red-700 text-white text-lg py-6"
                >
                  {isSubmitting ? 'Submitting...' : (
                    <>
                      Send Referral
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-sm text-gray-600 text-center">
                  By submitting, you confirm that you have permission to share your friend's contact information
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-8">Referral Program FAQ</h2>
          <div className="space-y-4">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2">When do I receive my discount?</h3>
                <p className="text-gray-600">
                  You'll receive your 10% discount code via email as soon as your friend books and completes their first cleaning service.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2">Is there a limit to referrals?</h3>
                <p className="text-gray-600">
                  No limit! Refer as many friends as you'd like. Each successful referral earns you another 10% discount.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2">Can I combine discounts?</h3>
                <p className="text-gray-600">
                  Yes! You can stack multiple referral discounts on a single service, up to 50% off total.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2">Do referral codes expire?</h3>
                <p className="text-gray-600">
                  Your referral discount codes are valid for 12 months from the date they're issued.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Start Saving <span className="text-red-600">Today!</span>
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Your friends will thank you, and so will your wallet
          </p>
          <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white">
            <a href="#referral-form" className="flex items-center">
              Refer Now
              <Gift className="w-5 h-5 ml-2" />
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ReferralPage;
